<template>
  <div class="hysteresis-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$B = μ₀(H + M)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>外磁场 H (励磁)</label>
            <span class="val">{{ hField.toFixed(1) }} <small>A/m</small></span>
          </div>
          <div class="hud-item orange">
            <label>磁感应强度 B</label>
            <span class="val">{{ bField.toFixed(2) }} <small>T</small></span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 450 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-loop"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 坐标轴 -->
          <g stroke="#334155" stroke-width="1.5">
             <line x1="50" y1="130" x2="400" y2="130" /> <!-- H-axis -->
             <line x1="225" y1="30" x2="225" y2="230" /> <!-- B-axis -->
          </g>
          <text x="390" y="145" fill="#475569" font-size="10" font-weight="900">强度 H</text>
          <text x="235" y="40" fill="#475569" font-size="10" font-weight="900">感应 B</text>

          <!-- 静态磁滞回线背景 (全闭合参考) -->
          <path :d="fullLoopPath" fill="none" stroke="#1e293b" stroke-width="2" stroke-dasharray="4,2" />

          <!-- 动态历史轨迹 (Hysteresis Path) -->
          <path :d="dynamicPath" fill="none" stroke="#0ea5e9" stroke-width="4" filter="url(#glow-loop)" opacity="0.8" />
          
          <!-- 当前磁化状态点 -->
          <circle :cx="225 + hField * 1.5" :cy="130 - bField * 80" r="6" fill="#fbbf24" filter="url(#glow-loop)" />

          <!-- 内部磁畴模拟区 (Domain Simulation) -->
          <g transform="translate(340, 60)">
             <rect width="80" height="80" fill="rgba(15,23,42,0.8)" rx="4" />
             <g v-for="i in 9" :key="'d'+i" :transform="`translate(${(i-1)%3*25+15}, ${Math.floor((i-1)/3)*25+15}) rotate(${bField * 60 + (Math.random()-0.5)*10})`">
                <line x1="-8" y1="0" x2="8" y2="0" stroke="#f43f5e" stroke-width="2" marker-end="url(#arr-d)" />
             </g>
             <text x="40" y="95" text-anchor="middle" fill="#64748b" font-size="9">内部磁畴取向表征</text>
          </g>

          <defs>
             <marker id="arr-d" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#f43f5e" /></marker>
          </defs>
        </svg>

        <div class="physics-caption">
           规律：调节 H 轴，你会发现 B 的变化滞后于 H。当 H 回到 0 时，B 并不为 0 (剩磁 Br)。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">激励磁场 (励磁电流 I ∝ H)</label>
                 <el-slider v-model="hField" :min="-100" :max="100" @input="updateB"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="info" size="mini" plain @click="reset">退磁 (Demagnetize)</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-discovery" v-latex>
           <i class="el-icon-magnet"></i>
           <p><strong>实战物理：</strong>铁磁物质为何能做永磁体？<strong>观察：</strong>将 H 推向 100 饱和后再拉回 0，纵轴截距就是**剩磁**。这说明材料记住了曾被磁化的历史。如果想让磁性消失，必须反向施加**矫顽力**。这解释了为何硬盘能存数据，也解释了为何变压器铁芯会发热（回线面积即是涡流损耗）。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'MagneticHysteresisViz',
  data() {
    return {
      hField: 0,
      bField: 0,
      history: [{h: 0, b:0}],
      ascending: true
    }
  },
  computed: {
    fullLoopPath() {
       let p = "M 50,220 ";
       // Math model for a typical hysteresis loop: B = tanh(H + loss * sign)
       for(let h=-100; h<=100; h+=5) { 
          let b = Math.tanh(h/30 + 0.8) * 1.0;
          p += `L ${225 + h*1.5},${130 - b*80} `;
       }
       for(let h=100; h>=-100; h-=5) {
          let b = Math.tanh(h/30 - 0.8) * 1.0;
          p += `L ${225 + h*1.5},${130 - b*80} `;
       }
       return p + "Z";
    },
    dynamicPath() {
       if (this.history.length < 2) return "";
       let p = `M ${225 + this.history[0].h * 1.5},${130 - this.history[0].b * 80}`;
       this.history.forEach(pt => {
          p += ` L ${225 + pt.h * 1.5},${130 - pt.b * 80}`;
       });
       return p;
    }
  },
  methods: {
    updateB(val) {
       // Hysteresis math logic: path depends on movement direction
       const prevH = this.history[this.history.length-1].h;
       this.ascending = val > prevH;
       const shift = this.ascending ? 0.8 : -0.8;
       this.bField = Math.tanh(val/30 + shift) * 1.0;
       
       this.history.push({ h: val, b: this.bField });
       if (this.history.length > 100) this.history.shift();
    },
    reset() {
       this.hField = 0;
       this.bField = 0;
       this.history = [{h: 0, b:0}];
    }
  }
}
</script>

<style scoped lang="scss">
.hysteresis-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fee2e2; color: #991b1b; border: 1px solid #fecdd3; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; } .btn-group { display: flex; gap: 8px; }

/* 重点加固：高对比度解释卡片，彻底解决文字看不清问题 */
.discovery-card-discovery { 
  background: #f1f5f9; // 深石墨蓝背景
  padding: 12px 16px; 
  border-radius: 10px; 
  border: 1px solid #e2e8f0; 
  display: flex; gap: 14px; 
  align-items: center;
  i { color: #334155; font-size: 1.4rem; }
  p { 
    margin: 0; 
    font-size: 11.5px; 
    color: #0f172a; // 午夜蓝文字，极高对比度
    line-height: 1.6;
    strong { color: #1e293b; font-weight: 900; }
  }
}
</style>
